import type { FC, ReactNode } from 'react'
import type { Course } from '@/lib/plans'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useState } from 'react'
import {
    BookOpen,
    Crown,
    Dumbbell,
    Ellipsis,
    FlaskConical,
    House,
    Info,
    Library,
    ScrollText,
    Sparkles,
    TrendingUp
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { isActive } from '@/components/app/app-header'
import { SettingsPanel } from '@/components/app/settings-controls'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'

type KidTone = 'orange' | 'sky' | 'grass' | 'berry' | 'grape' | 'sun'

/** Kids mode only (applied before paint through html[data-mode]): each item has its own colour, the current one a filled pill. */
const KIDS_TONE: Record<KidTone, { icon: string, bubble: string, pill: string }> = {
    orange: {
        icon: 'in-data-[mode=kids]:text-kid-orange-ink',
        bubble: 'in-data-[mode=kids]:bg-kid-orange/14',
        pill: 'in-data-[mode=kids]:bg-kid-orange'
    },
    sky: {
        icon: 'in-data-[mode=kids]:text-kid-sky-ink',
        bubble: 'in-data-[mode=kids]:bg-kid-sky/14',
        pill: 'in-data-[mode=kids]:bg-kid-sky'
    },
    grass: {
        icon: 'in-data-[mode=kids]:text-kid-grass-ink',
        bubble: 'in-data-[mode=kids]:bg-kid-grass/14',
        pill: 'in-data-[mode=kids]:bg-kid-grass'
    },
    berry: {
        icon: 'in-data-[mode=kids]:text-kid-berry-ink',
        bubble: 'in-data-[mode=kids]:bg-kid-berry/14',
        pill: 'in-data-[mode=kids]:bg-kid-berry'
    },
    grape: {
        icon: 'in-data-[mode=kids]:text-kid-grape-ink',
        bubble: 'in-data-[mode=kids]:bg-kid-grape/14',
        pill: 'in-data-[mode=kids]:bg-kid-grape'
    },
    sun: {
        icon: 'in-data-[mode=kids]:text-kid-sun-ink',
        bubble: 'in-data-[mode=kids]:bg-kid-sun/30',
        pill: 'in-data-[mode=kids]:bg-kid-sun'
    }
}

const KIDS_ICON_BUBBLE = 'kid-nav-icon flex items-center justify-center rounded-full in-data-[mode=kids]:h-9 in-data-[mode=kids]:w-14'

interface BarItem {
    href: string
    en: string
    icon: typeof House
    tone: KidTone
}

/** The same bar in both courses: four sections, then More. */
const BARS: Record<Course, { main: BarItem[], more: BarItem[] }> = {
    prakrit: {
        main: [
            { href: '/prakrit', en: 'Home', icon: House, tone: 'orange' },
            { href: '/learn', en: 'Learn', icon: BookOpen, tone: 'sky' },
            { href: '/practice', en: 'Practice', icon: Dumbbell, tone: 'grass' },
            { href: '/grammar', en: 'Grammar', icon: FlaskConical, tone: 'berry' }
        ],
        more: [
            { href: '/vocabulary', en: 'Vocabulary', icon: Library, tone: 'grape' },
            { href: '/stories', en: 'Stories', icon: ScrollText, tone: 'sun' },
            { href: '/progress', en: 'Progress', icon: TrendingUp, tone: 'grass' },
            { href: '/pricing', en: 'Premium', icon: Crown, tone: 'orange' },
            { href: '/about', en: 'About and sources', icon: Info, tone: 'sky' },
            { href: '/', en: 'Suvaani home', icon: Sparkles, tone: 'grape' }
        ]
    },
    sanskrit: {
        main: [
            { href: '/sanskrit', en: 'Home', icon: House, tone: 'orange' },
            { href: '/sanskrit/learn', en: 'Learn', icon: BookOpen, tone: 'sky' },
            { href: '/sanskrit/practice', en: 'Practice', icon: Dumbbell, tone: 'grass' },
            { href: '/sanskrit/grammar', en: 'Grammar', icon: FlaskConical, tone: 'berry' }
        ],
        more: [
            { href: '/sanskrit/vocabulary', en: 'Vocabulary', icon: Library, tone: 'grape' },
            { href: '/sanskrit/stories', en: 'Stories', icon: ScrollText, tone: 'sun' },
            { href: '/sanskrit/progress', en: 'Progress', icon: TrendingUp, tone: 'grass' },
            { href: '/pricing', en: 'Premium', icon: Crown, tone: 'orange' },
            { href: '/sanskrit/about/content', en: 'Sources and verification', icon: Info, tone: 'sky' },
            { href: '/', en: 'Suvaani home', icon: Sparkles, tone: 'grape' }
        ]
    }
}

/** Mobile and tablet navigation for either course: Home · Learn · Practice · Grammar · More (English only). */
export const BottomNav: FC<{ course?: Course }> = ({ course = 'prakrit' }): ReactNode => {
    const { main: MAIN, more: MORE } = BARS[course]
    const router = useRouter()
    const [more, setMore] = useState(false)
    const moreActive = MORE.some(m => isActive(router.pathname, m.href))

    return (
        <>
            <nav
                aria-label='Primary (mobile)'
                className='fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 pb-[env(safe-area-inset-bottom)] backdrop-blur xl:hidden in-data-[mode=kids]:border-transparent'
            >
                {/* Kids mode: a scalloped rainbow edge on top of the bar (decorative). */}
                <span aria-hidden='true' className='kids-only kid-bunting kid-bunting-up pointer-events-none absolute inset-x-0 bottom-full' />
                <ul className='mx-auto grid max-w-xl grid-cols-5'>
                    {MAIN.map(item => {
                        const active = isActive(router.pathname, item.href)
                        const Icon = item.icon
                        const tone = KIDS_TONE[item.tone]

                        return (
                            <li key={item.href}>
                                <Link
                                    href={item.href}
                                    aria-current={active ? 'page' : undefined}
                                    className={cn(
                                        'flex min-h-16 flex-col items-center justify-center gap-0.5 px-1 text-muted-foreground',
                                        active && 'text-ink in-data-[mode=kids]:text-foreground'
                                    )}
                                >
                                    <span aria-hidden='true' className={cn(KIDS_ICON_BUBBLE, active ? tone.pill : tone.bubble)}>
                                        <Icon
                                            className={cn('size-5 in-data-[mode=kids]:size-6', active ? 'text-saffron-strong in-data-[mode=kids]:text-kid-on' : tone.icon)}
                                            aria-hidden='true'
                                        />
                                    </span>
                                    <span className={cn('text-[0.8125rem] font-medium leading-tight', active && 'font-semibold in-data-[mode=kids]:font-bold')}>{item.en}</span>
                                </Link>
                            </li>
                        )
                    })}
                    <li>
                        <button
                            type='button'
                            onClick={() => setMore(true)}
                            aria-expanded={more}
                            className={cn(
                                'flex min-h-16 w-full flex-col items-center justify-center gap-0.5 px-1 text-muted-foreground',
                                moreActive && 'text-ink in-data-[mode=kids]:text-foreground'
                            )}
                        >
                            <span aria-hidden='true' className={cn(KIDS_ICON_BUBBLE, moreActive ? KIDS_TONE.grape.pill : KIDS_TONE.grape.bubble)}>
                                <Ellipsis
                                    className={cn('size-5 in-data-[mode=kids]:size-6', moreActive ? 'in-data-[mode=kids]:text-kid-on' : KIDS_TONE.grape.icon)}
                                    aria-hidden='true'
                                />
                            </span>
                            <span lang='en' className={cn('text-[0.8125rem] font-medium leading-tight', moreActive && 'in-data-[mode=kids]:font-bold')}>More</span>
                        </button>
                    </li>
                </ul>
            </nav>

            <Sheet open={more} onOpenChange={setMore}>
                <SheetContent side='bottom' className='max-h-[85vh] overflow-y-auto rounded-t-2xl'>
                    <SheetHeader>
                        <SheetTitle>More</SheetTitle>
                    </SheetHeader>
                    <div className='flex flex-col gap-6 px-4 pb-8'>
                        <ul className='grid grid-cols-2 gap-2'>
                            {MORE.map(item => {
                                const Icon = item.icon
                                const tone = KIDS_TONE[item.tone]

                                return (
                                    <li key={item.href}>
                                        <Link
                                            href={item.href}
                                            onClick={() => setMore(false)}
                                            className='flex min-h-tap items-center gap-3 rounded-xl border border-border bg-card px-3 py-3 in-data-[mode=kids]:border-2'
                                        >
                                            <span aria-hidden='true' className={cn('kid-nav-icon flex shrink-0 items-center justify-center rounded-full in-data-[mode=kids]:size-10', tone.bubble)}>
                                                <Icon className={cn('size-5 text-saffron-strong in-data-[mode=kids]:size-6', tone.icon)} aria-hidden='true' />
                                            </span>
                                            <span className='font-medium leading-tight'>{item.en}</span>
                                        </Link>
                                    </li>
                                )
                            })}
                        </ul>
                        <SettingsPanel showMode />
                    </div>
                </SheetContent>
            </Sheet>
        </>
    )
}
